import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import { MainLayout } from 'views/layouts'
import { userSelector } from 'ducks/user'
import { headers } from './constants'
import { H3 } from 'views/components/UI/Text'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useNotification } from 'lib/context/notification.context'
import { BackButton, Button } from 'views/components/UI/Buttons'
import { requestRenewalEmail } from 'services/inventory/requestRenewalEmail'
import {
  getInventoryCompanies,
  getManagedInventories,
  InventoryCompany,
  ManagedInventory,
} from 'services/inventory/management'
import InventoryLedger from 'views/screens/super-admin/inventory/ledger'
import { inventoryTabsSx } from 'views/components/UI/InventoryTabs/styles'
import { platformAdminRoutes } from 'router/routes'
import InventoryInvoicesDialog from './inventory-invoices-dialog'

type InventoryRow = {
  id: number
  name: string
  type: string
  companyName: string
  credits: number | string
  tests_used: number
  tests_available: number | string
  start_date: string
  expiration_date: string
  state: string
  isUnlimited: boolean
}

dayjs.extend(utc)

const formatInventory = (item: ManagedInventory): InventoryRow => ({
  id: Number(item.id),
  name: item.subscriptionName || item.name,
  type: item.name,
  companyName: item.companyName,
  credits: item.isUnlimited ? '∞' : Number(item.credits),
  tests_used: Math.min(Number(item.usedCredits || 0), Number(item.credits)),
  tests_available: item.isUnlimited ? '∞' : Math.max(0, Number(item.creditsAvailable)),
  start_date: dayjs.utc(item.start_date).format('DD/MM/YYYY'),
  expiration_date: dayjs.utc(item.expiration_date).format('DD/MM/YYYY'),
  state: item.state ? 'Activo' : 'Inactivo',
  isUnlimited: item.isUnlimited,
})

const InventoryScreen: FC = () => {
  const navigate = useNavigate()
  const { companyId: routeCompanyId } = useParams()
  const { companyId } = useSelector(userSelector)
  const { getSuccess, getWarning } = useNotification()
  const [companies, setCompanies] = useState<InventoryCompany[]>([])
  const [internal, setInternal] = useState<boolean>(false)
  const [accessLoaded, setAccessLoaded] = useState<boolean>(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)
  const [inventoryResult, setInventoryResult] = useState<InventoryRow[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPage, setTotalPage] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedLedger, setSelectedLedger] = useState<InventoryRow | null>(null)
  const [selectedInvoices, setSelectedInvoices] = useState<InventoryRow | null>(null)
  const [ledgerTab, setLedgerTab] = useState<'processes' | 'consumption'>('processes')

  useEffect(() => {
    let active = true
    getInventoryCompanies()
      .then((response) => {
        if (!active) return
        const requestedId = Number(routeCompanyId)
        const allowedRequestedId = response.data.some((item: InventoryCompany) => item.id === requestedId)
        const ownCompany = response.data.find((item: InventoryCompany) => item.id === Number(companyId))
        let nextCompanyId: number | null = ownCompany?.id || response.data[0]?.id || null
        if (response.internal && routeCompanyId === 'all') nextCompanyId = null
        else if (allowedRequestedId) nextCompanyId = requestedId
        setCompanies(response.data)
        setInternal(response.internal)
        setSelectedCompanyId(nextCompanyId)
        setAccessLoaded(true)
      })
      .catch(() => {
        if (active) {
          setAccessLoaded(false)
          getWarning('No se pudieron cargar las empresas disponibles')
        }
      })
    return () => { active = false }
  }, [routeCompanyId, companyId, getWarning])

  useEffect(() => {
    if (!accessLoaded) return
    let active = true
    setIsLoading(true)
    getManagedInventories(selectedCompanyId, currentPage, DEFAULT_LIMIT)
      .then((response) => {
        if (!active) return
        setInventoryResult(response.data.map(formatInventory))
        setTotalPage(response.total_pages)
      })
      .catch(() => {
        if (active) {
          setInventoryResult([])
          setTotalPage(0)
          getWarning('No se pudieron cargar los inventarios')
        }
      })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [accessLoaded, selectedCompanyId, currentPage, getWarning])

  const handleRenewInventory = async (inventory: InventoryRow): Promise<void> => {
    if (!window.confirm('¿Quieres solicitar la renovación de este inventario?')) return
    try {
      setIsLoading(true)
      await requestRenewalEmail({
        mail: 'sararuiz@bluesitehr.com',
        type: 'inventory_renewal_request',
        company: inventory.companyName,
        inventoryName: inventory.name,
        inventoryStatus: inventory.state,
        availableCredits: inventory.isUnlimited ? 'Ilimitado' : inventory.tests_available,
        usedCredits: inventory.tests_used,
        expirationDate: inventory.expiration_date,
        message: 'El administrador solicita gestionar la renovación del inventario durante 12 meses.',
      })
      getSuccess('Solicitud de renovación enviada')
    } catch (error) {
      getWarning('No se pudo solicitar la renovación')
    } finally {
      setIsLoading(false)
    }
  }

  const InventoryActions: FC<InventoryRow> = (row: InventoryRow) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Button type="button" size="sm" variant="secondary" onClick={() => handleRenewInventory(row)}>
        Solicitar renovación
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={() => setSelectedInvoices(row)}>
        Facturas
      </Button>
      {internal && <Tooltip title="Ver procesos y consumo del inventario">
        <IconButton aria-label="Ver procesos y consumo del inventario" onClick={() => { setSelectedLedger(row); setLedgerTab('processes') }}>
          <AccountBalanceOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>}
    </div>
  )

  const handleCompanyChange = (value: string): void => {
    setCurrentPage(1)
    setSelectedLedger(null)
    navigate(platformAdminRoutes.Inventory.replace(':companyId', value))
  }

  return (
    <MainLayout>
      {selectedLedger ? <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '12px 0 20px' }}>
          <BackButton onClick={() => setSelectedLedger(null)} />
          <H3>Consumo del inventario: {selectedLedger.name}</H3>
        </div>
        <Box sx={{ background: '#fff', borderRadius: '10px', px: 1, mb: 2 }}>
          <Tabs value={ledgerTab} onChange={(_event: React.SyntheticEvent, value: 'processes' | 'consumption') => setLedgerTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Procesos y consumo del inventario" sx={inventoryTabsSx}>
            <Tab label="Procesos" value="processes" />
            <Tab label="Consumo del inventario" value="consumption" />
          </Tabs>
        </Box>
        <InventoryLedger inventoryId={selectedLedger.id} view={ledgerTab} />
      </> : <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '40px 0 10px' }}>
          <BackButton onClick={() => navigate(-1)} />
          <H3>Inventarios</H3>
        </div>
        {accessLoaded && <div style={{ marginBottom: 16 }}>
          <label htmlFor="inventory-company">Empresa: </label>
          <select id="inventory-company" value={selectedCompanyId || 'all'} onChange={(event) => handleCompanyChange(event.target.value)} style={{ minWidth: 240, padding: 8, borderRadius: 6, border: '1px solid #b8c7d1' }}>
            {internal && <option value="all">Todas las empresas</option>}
            {!internal && companies.length === 0 && <option value="all">Sin empresas disponibles</option>}
            {companies.map((item: InventoryCompany) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </div>}
        <div style={{ marginBottom: 30 }}>
          <BluesiteTableComponent
            data={inventoryResult}
            headers={headers}
            ActionComponent={InventoryActions}
            totalPages={totalPage}
            handlePagination={setCurrentPage}
            currentPage={currentPage}
            searchBar={false}
            searchLabel=""
            minimumColumnWidth={145}
            tableStyle="inventory"
          />
        </div>
      </>}
      <ModalLoader isLoading={isLoading} />
      {selectedInvoices && <InventoryInvoicesDialog inventory={selectedInvoices} onClose={() => setSelectedInvoices(null)} />}
    </MainLayout>
  )
}

export default InventoryScreen
